<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import crmAPI from '../api/crm';
import { registerCrmLocales } from '../i18n';
import KanbanBoard from '../components/KanbanBoard.vue';
import DealModal from '../components/DealModal.vue';
import DealChatDrawer from '../components/DealChatDrawer.vue';
import PipelineSettingsModal from '../components/PipelineSettingsModal.vue';
import CrmDropdown from '../components/CrmDropdown.vue';

// Scheduling Engine Integration
import schedulingAPI from '../../scheduling/api/scheduling';
import EnrollmentModal from '../../scheduling/components/EnrollmentModal.vue';
import AppointmentModal from '../../scheduling/components/AppointmentModal.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const i18n = useI18n();

registerCrmLocales(i18n);

const accountId = computed(() => route.params.accountId || store.getters.getCurrentAccountId);

const pipelines = ref([]);
const deals = ref([]);
const selectedPipelineId = ref(null);
const selectedUserId = ref('');
const selectedInboxId = ref('');
const searchQuery = ref('');
const searchInputRef = ref(null);
const isLoading = ref(true);

const showDealModal = ref(false);
const showSettingsModal = ref(false);
const editingDeal = ref(null);
const selectedDealForChat = ref(null);
const newDealInitialStageId = ref(null);

// Scheduling State & Meta
const showScheduleModal = ref(false);
const scheduleModalType = ref('enrollment');
const scheduleInitialData = ref(null);
const activeDealForSchedule = ref(null);

const schedulingServices = ref([]);
const schedulingProfessionals = ref([]);
const schedulingPatients = ref([]);

const currentPipeline = computed(() => {
  return (
    pipelines.value.find(p => Number(p.id) === Number(selectedPipelineId.value)) ||
    pipelines.value[0] || { stages: [] }
  );
});

const agents = computed(() => store.getters['agents/getAgents'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const pipelineOptions = computed(() => {
  return pipelines.value.map(p => ({
    value: p.id,
    label: p.name,
    count: p.deals_count || 0,
  }));
});

const agentOptions = computed(() => {
  return [
    { value: '', label: 'Todos os agentes' },
    ...agents.value.map(a => ({
      value: a.id,
      label: a.name,
    })),
  ];
});

const inboxOptions = computed(() => {
  return [
    { value: '', label: 'Todas as caixas' },
    ...inboxes.value.map(i => ({
      value: i.id,
      label: i.name,
    })),
  ];
});

const filteredDeals = computed(() => {
  let result = deals.value;

  if (selectedPipelineId.value) {
    result = result.filter(d => Number(d.pipeline_id) === Number(selectedPipelineId.value));
  }

  if (selectedUserId.value) {
    result = result.filter(d => Number(d.user_id) === Number(selectedUserId.value));
  }

  if (selectedInboxId.value) {
    result = result.filter(
      d => d.conversation && Number(d.conversation.inbox_id) === Number(selectedInboxId.value)
    );
  }

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase().trim();
    result = result.filter(d => {
      const matchTitle = (d.title || '').toLowerCase().includes(q);
      const matchContact =
        d.contact &&
        (d.contact.name || d.contact.email || d.contact.phone_number || '')
          .toLowerCase()
          .includes(q);
      return matchTitle || matchContact;
    });
  }

  return result;
});

const fetchPipelines = async () => {
  try {
    const res = await crmAPI.getPipelines();
    pipelines.value = res.data || [];
    store.dispatch('crm/fetchPipelines');

    if (route.params.pipelineId) {
      selectedPipelineId.value = Number(route.params.pipelineId);
    } else if (pipelines.value.length > 0 && !selectedPipelineId.value) {
      const defaultPipe = pipelines.value.find(p => p.is_default) || pipelines.value[0];
      selectedPipelineId.value = defaultPipe.id;
    }
  } catch (err) {
    console.error('Error fetching pipelines:', err);
  }
};

const fetchDeals = async () => {
  if (!selectedPipelineId.value) return;
  isLoading.value = true;
  try {
    const res = await crmAPI.getDeals({ pipeline_id: selectedPipelineId.value });
    deals.value = res.data || [];
  } catch (err) {
    console.error('Error fetching deals:', err);
  } finally {
    isLoading.value = false;
  }
};

const loadSchedulingMeta = async () => {
  try {
    const [srvs, profs, pats] = await Promise.all([
      schedulingAPI.getServices().catch(() => ({ data: [] })),
      schedulingAPI.getProfessionals().catch(() => ({ data: [] })),
      schedulingAPI.getPatients().catch(() => ({ data: [] })),
    ]);
    schedulingServices.value = srvs.data || [];
    schedulingProfessionals.value = profs.data || [];
    schedulingPatients.value = pats.data || [];
  } catch (err) {
    console.error('Erro ao carregar metadados de agendamentos:', err);
  }
};

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  await fetchPipelines();
  loadSchedulingMeta();
});

watch(
  () => route.params.pipelineId,
  newPipeId => {
    if (newPipeId && Number(newPipeId) !== Number(selectedPipelineId.value)) {
      selectedPipelineId.value = Number(newPipeId);
    }
  }
);

watch(selectedPipelineId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    fetchDeals();
  }
});

const onPipelineChange = id => {
  if (selectedPipelineId.value === id) return;
  selectedPipelineId.value = id;
  router.replace({
    name: 'crm_pipeline_view',
    params: { accountId: accountId.value, pipelineId: id },
  }).catch(() => {});
};

// Scheduling Drafts Cache por Deal
const dealScheduleDrafts = ref({});

const triggerScheduleForDeal = async (deal, extraScheduleParams = {}) => {
  activeDealForSchedule.value = deal;

  if (schedulingServices.value.length === 0) {
    await loadSchedulingMeta();
  }

  const existingDraft = dealScheduleDrafts.value[deal.id] || {};
  const contact = deal.contact || {};
  const contactName = existingDraft.patient_name || contact.name || deal.title || '';
  const contactPhone = existingDraft.patient_phone || contact.phone_number || '';
  const contactEmail = existingDraft.patient_email || contact.email || '';
  const convId = deal.conversation_id || deal.conversation?.id || null;

  scheduleInitialData.value = {
    patient_name: contactName,
    patient_phone: contactPhone,
    patient_email: contactEmail,
    conversation_id: convId,
    price: Number(deal.value) || 250,
    ...existingDraft,
    ...extraScheduleParams,
  };

  scheduleModalType.value = extraScheduleParams.session_type === 'single' ? 'appointment' : (existingDraft.session_type === 'single' ? 'appointment' : 'enrollment');
  showScheduleModal.value = true;
};

const onScheduleClose = draftData => {
  showScheduleModal.value = false;
  if (activeDealForSchedule.value && draftData && typeof draftData === 'object') {
    dealScheduleDrafts.value[activeDealForSchedule.value.id] = {
      ...scheduleInitialData.value,
      ...draftData,
    };
  }
};

const onScheduleSaved = async resultData => {
  showScheduleModal.value = false;

  if (activeDealForSchedule.value) {
    const deal = activeDealForSchedule.value;
    delete dealScheduleDrafts.value[deal.id];

    if (resultData) {
      const schedulePrice = Number(resultData.price || resultData.enrollment?.price || resultData.appointment?.price || 0);

      if ((!deal.value || Number(deal.value) === 0) && schedulePrice > 0) {
        try {
          await crmAPI.updateDeal(deal.id, { value: schedulePrice });
          deal.value = schedulePrice;
        } catch (err) {
          console.error('Erro ao atualizar valor do negócio com o agendamento:', err);
        }
      }
    }
  }

  await fetchDeals();
};

const handleMoveDeal = async ({ dealId, toStage }) => {
  const deal = deals.value.find(d => Number(d.id) === Number(dealId));
  if (!deal) return;

  const prevStageId = deal.pipeline_stage_id;
  deal.pipeline_stage_id = toStage.id;
  deal.status = toStage.stage_type;

  try {
    await crmAPI.moveDealStage(dealId, {
      pipeline_stage_id: toStage.id,
      position: 0,
    });
    store.dispatch('crm/fetchPipelines');

    if (toStage.stage_type === 'won') {
      triggerScheduleForDeal(deal);
    }
  } catch (err) {
    deal.pipeline_stage_id = prevStageId;
    alert('Erro ao mover negócio.');
  }
};

const handleScheduleFromDrawer = scheduleData => {
  const deal = scheduleData.deal || selectedDealForChat.value;
  if (!deal) return;

  triggerScheduleForDeal(deal, {
    day_key: scheduleData.day_of_week,
    day_of_week: scheduleData.day_of_week,
    time_slot: scheduleData.time_slot,
    scheduling_professional_id: scheduleData.professional_id,
    professional_id: scheduleData.professional_id,
    scheduling_service_id: scheduleData.service_id,
    service_id: scheduleData.service_id,
  });
};

const openNewDeal = (stage = null) => {
  editingDeal.value = null;
  newDealInitialStageId.value = stage ? stage.id : null;
  showDealModal.value = true;
};

const handleEditDeal = deal => {
  editingDeal.value = deal;
  showDealModal.value = true;
};

const handleEditStage = () => {
  showSettingsModal.value = true;
};

const handleOpenChat = deal => {
  selectedDealForChat.value = deal;
};

const onDealSaved = deal => {
  const idx = deals.value.findIndex(d => Number(d.id) === Number(deal.id));
  if (idx !== -1) {
    deals.value[idx] = deal;
  } else {
    deals.value.unshift(deal);
  }
  if (selectedDealForChat.value && Number(selectedDealForChat.value.id) === Number(deal.id)) {
    selectedDealForChat.value = deal;
  }
  store.dispatch('crm/fetchPipelines');
};

const onDealDeleted = dealId => {
  deals.value = deals.value.filter(d => Number(d.id) !== Number(dealId));
  if (selectedDealForChat.value && Number(selectedDealForChat.value.id) === Number(dealId)) {
    selectedDealForChat.value = null;
  }
  store.dispatch('crm/fetchPipelines');
};

const onPipelineUpdated = async () => {
  await fetchPipelines();
  await fetchDeals();
};

const onPipelineCreated = async newPipe => {
  await fetchPipelines();
  selectedPipelineId.value = newPipe.id;
  await fetchDeals();
};

const onPipelineDeleted = async () => {
  await fetchPipelines();
  if (pipelines.value.length > 0) {
    selectedPipelineId.value = pipelines.value[0].id;
    await fetchDeals();
  }
};
</script>

<template>
  <div class="flex-1 flex flex-col h-full overflow-hidden bg-n-background text-n-slate-12 select-none">
    <!-- Top Header Bar (Chatwoot v4 Standard) -->
    <header class="px-5 py-2.5 bg-n-surface-1 border-b border-n-weak flex items-center justify-between gap-3 min-h-[48px] select-none">
      <!-- Left: Pipeline Selector & Search -->
      <div class="flex items-center gap-2.5">
        <!-- Pipeline Switcher Dropdown -->
        <CrmDropdown
          v-if="pipelines.length > 1"
          :model-value="selectedPipelineId"
          :options="pipelineOptions"
          placeholder="Selecione o Funil"
          align="left"
          @change="onPipelineChange"
        >
          <template #icon>
            <span class="text-n-brand text-xs">📊</span>
          </template>
        </CrmDropdown>

        <!-- Pipeline Title Indicator (Single Pipeline) -->
        <div
          v-else
          class="inline-flex items-center gap-2 h-8 px-3 bg-n-alpha-1 border border-n-weak rounded-lg text-xs font-semibold text-n-slate-12 shadow-xs select-none"
        >
          <span class="text-n-brand text-xs">📊</span>
          <span class="truncate max-w-[170px]">{{ currentPipeline.name || 'Funil de Vendas' }}</span>
          <span class="px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-[10px] text-n-slate-11 border border-n-weak font-mono">
            {{ filteredDeals.length }}
          </span>
        </div>

        <!-- Overview Dashboard Link -->
        <router-link
          :to="{ name: 'crm_overview', params: { accountId } }"
          class="h-8 px-2.5 inline-flex items-center gap-1.5 bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak rounded-lg text-xs font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors cursor-pointer"
          title="Visão Geral & Métricas do CRM"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span class="hidden sm:inline">Visão Geral</span>
        </router-link>

        <!-- Search Input Pill -->
        <div
          class="inline-flex items-center gap-2 h-8 px-2.5 bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak focus-within:border-brand rounded-lg w-48 sm:w-56 transition-all cursor-text"
          @click="searchInputRef?.focus()"
        >
          <svg class="w-3.5 h-3.5 text-n-slate-10 shrink-0 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            placeholder="Buscar oportunidade..."
            class="w-full !bg-transparent !border-0 !p-0 !m-0 !outline-none text-xs text-n-slate-12 placeholder:text-n-slate-10 focus:ring-0"
          />
        </div>
      </div>

      <!-- Right: Filters, Settings, Add Deal -->
      <div class="flex items-center gap-2">
        <!-- Agent Filter Dropdown -->
        <CrmDropdown
          v-model="selectedUserId"
          :options="agentOptions"
          placeholder="Todos os agentes"
          align="right"
        >
          <template #icon>
            <svg class="w-3.5 h-3.5 text-purple-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </template>
        </CrmDropdown>

        <!-- Inbox Filter Dropdown -->
        <CrmDropdown
          v-model="selectedInboxId"
          :options="inboxOptions"
          placeholder="Todas as caixas"
          align="right"
        >
          <template #icon>
            <svg class="w-3.5 h-3.5 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </template>
        </CrmDropdown>

        <!-- Settings Cog Button -->
        <button
          type="button"
          class="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak text-n-slate-11 hover:text-n-slate-12 transition-colors cursor-pointer shadow-xs"
          title="Configurações do Funil"
          @click="showSettingsModal = true"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>

        <!-- Main Action Button -->
        <button
          type="button"
          class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-brand hover:bg-brand-hover active:bg-brand text-xs font-medium text-white shadow-xs transition-colors cursor-pointer"
          @click="openNewDeal()"
        >
          <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          <span>Nova Oportunidade</span>
        </button>
      </div>
    </header>

    <!-- Main Kanban Scroll Area -->
    <main class="flex-1 overflow-hidden relative">
      <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-xs z-10">
        <div class="animate-spin rounded-full h-7 w-7 border-2 border-brand border-t-transparent"></div>
      </div>

      <KanbanBoard
        v-if="currentPipeline && currentPipeline.id"
        :pipeline="currentPipeline"
        :deals="filteredDeals"
        @move-deal="handleMoveDeal"
        @edit-deal="handleEditDeal"
        @click-deal="handleOpenChat"
        @new-deal-in-stage="openNewDeal"
        @edit-stage="handleEditStage"
      />
    </main>

    <!-- Chat & Details Drawer -->
    <DealChatDrawer
      :deal="selectedDealForChat"
      :stages="currentPipeline.stages"
      :account-id="accountId"
      @close="selectedDealForChat = null"
      @deal-updated="onDealSaved"
      @edit-deal="handleEditDeal"
      @open-schedule-modal="handleScheduleFromDrawer"
    />

    <!-- Modals -->
    <DealModal
      :show="showDealModal"
      :deal="editingDeal"
      :pipeline="currentPipeline"
      :initial-stage-id="newDealInitialStageId"
      @close="showDealModal = false"
      @saved="onDealSaved"
      @deleted="onDealDeleted"
    />

    <PipelineSettingsModal
      :show="showSettingsModal"
      :pipelines="pipelines"
      :selected-pipeline-id="selectedPipelineId"
      @close="showSettingsModal = false"
      @pipeline-updated="onPipelineUpdated"
      @pipeline-created="onPipelineCreated"
      @pipeline-deleted="onPipelineDeleted"
    />

    <!-- Matrícula / Agendamento Direto no CRM -->
    <EnrollmentModal
      v-if="showScheduleModal && scheduleModalType === 'enrollment'"
      :services="schedulingServices"
      :professionals="schedulingProfessionals"
      :patients="schedulingPatients"
      :initial-data="scheduleInitialData"
      @close="onScheduleClose"
      @saved="onScheduleSaved"
    />

    <AppointmentModal
      v-if="showScheduleModal && scheduleModalType === 'appointment'"
      :services="schedulingServices"
      :professionals="schedulingProfessionals"
      :patients="schedulingPatients"
      :initial-data="scheduleInitialData"
      @close="onScheduleClose"
      @saved="onScheduleSaved"
    />
  </div>
</template>
